import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useAuth from 'src/hooks/useAuth';
import { ShoppingList } from 'src/types/view_shopping_lists/ShoppingList';
import {
  initialShoppingListFormValues,
  ShoppingListFormField,
  ShoppingListFormValues,
} from 'src/types/view_shopping_lists/ShoppingListForm';
import { useContextSelector } from 'use-context-selector';
import { ViewShoppingListsContext } from './ViewShoppingListsContext';

type ShoppingListFormMode = 'create' | 'edit';

interface UseShoppingListFormOptions {
  mode: ShoppingListFormMode;
  isOpen: boolean;
  shoppingList?: ShoppingList | null;
  onSuccess?: () => void;
}

export const useShoppingListForm = ({
  mode,
  isOpen,
  shoppingList = null,
  onSuccess,
}: UseShoppingListFormOptions) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const upsertShoppingList = useContextSelector(
    ViewShoppingListsContext,
    (context) => context.upsertShoppingList
  );
  const isUpsertingShoppingList = useContextSelector(
    ViewShoppingListsContext,
    (context) => context.isUpsertingShoppingList
  );
  const [values, setValues] = useState<ShoppingListFormValues>(initialShoppingListFormValues);
  const [initialValues, setInitialValues] = useState<ShoppingListFormValues>(
    initialShoppingListFormValues
  );
  const [touched, setTouched] = useState<Partial<Record<ShoppingListFormField, boolean>>>({});
  const wasOpenRef = useRef(false);

  const buildInitialValues = useCallback((): ShoppingListFormValues => {
    if (mode === 'edit' && shoppingList) {
      return {
        listName: shoppingList.title,
        owner: shoppingList.owner,
        glCode: shoppingList.glCode,
      };
    }

    return {
      listName: '',
      owner: user.ContactName ?? '',
      glCode: '',
    };
  }, [mode, shoppingList, user.ContactName]);

  const resetForm = useCallback(() => {
    const nextValues = buildInitialValues();
    setValues(nextValues);
    setInitialValues(nextValues);
    setTouched({});
  }, [buildInitialValues]);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      resetForm();
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, resetForm]);

  const setFieldValue = useCallback((field: ShoppingListFormField, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFieldBlur = useCallback((field: ShoppingListFormField) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  const errors = useMemo(() => {
    const nextErrors: Partial<Record<ShoppingListFormField, string>> = {};

    if (!values.listName.trim()) {
      nextErrors.listName = t('List Name is required');
    }

    return nextErrors;
  }, [t, values.listName]);

  const isDirty = useMemo(
    () =>
      values.listName !== initialValues.listName || values.glCode !== initialValues.glCode,
    [initialValues.glCode, initialValues.listName, values.glCode, values.listName]
  );

  const isValid = useMemo(() => Object.keys(errors).length === 0, [errors]);

  const handleSave = useCallback(async () => {
    setTouched({ listName: true });
    if (!isValid) {
      return;
    }

    const favoriteCategoryId =
      mode === 'edit' && shoppingList ? Number(shoppingList.id) : null;

    if (mode === 'edit' && (favoriteCategoryId == null || Number.isNaN(favoriteCategoryId))) {
      toast.error(t('Failed to save shopping list'));
      return;
    }

    try {
      await upsertShoppingList({
        favoriteCategoryId,
        description: values.listName.trim(),
        glCode: values.glCode || null,
      });
      onSuccess?.();
    } catch {
      toast.error(t('Failed to save shopping list'));
    }
  }, [
    isValid,
    mode,
    onSuccess,
    shoppingList,
    t,
    upsertShoppingList,
    values.glCode,
    values.listName,
  ]);

  return {
    values,
    errors,
    touched,
    isDirty,
    isValid,
    isSaving: isUpsertingShoppingList,
    setFieldValue,
    handleFieldBlur,
    handleSave,
  };
};
